import type { ReactNode } from 'react';

import { Dropdown } from 'primereact/dropdown';

import { useGetTechsQuery, useGetVetsQuery } from '@/app/services/inClinic';
import { useGetYardsQuery as useGetYardsGql } from '@/app/services/lookups';
import { DatePicker } from '@/components/ui/date-picker';
import { useSites } from '@/services/sites';

import { CHUTE_OPTIONS } from '../../constants';
import type { ImplantSession } from '../../hooks/useImplantSession';
import type { LookupItem } from '../../types';
import { toWallClockDate } from '@/utils/wallClock';

interface ImplantToolbarProps {
  session: ImplantSession;
  readonly?: boolean;
  actions?: ReactNode;
}

export function ImplantToolbar({
  session,
  readonly = false,
  actions,
}: ImplantToolbarProps) {
  const { data: sitesRaw, isLoading: sitesLoading } = useSites({
    filter: 'fertilization',
  });
  const { data: yardsRaw, isFetching: yardsLoading } = useGetYardsGql({});
  const sites = sitesRaw ?? [];
  const yards = yardsRaw ?? [];
  const { data: vets = [], isLoading: vetsLoading } = useGetVetsQuery();
  const { data: techs = [], isLoading: techsLoading } = useGetTechsQuery();

  const dateValue = session.impDate
    ? new Date(session.impDate + 'T00:00:00')
    : undefined;

  return (
    <div className="bg-muted/30 ring-border flex flex-wrap items-end gap-x-3 gap-y-2 rounded-lg px-3 py-2 ring-1 lg:sticky lg:top-0 lg:z-10">
      <div className="flex flex-col gap-1">
        <span className="text-muted-foreground text-[11px] font-medium tracking-wide uppercase">
          Date
        </span>
        <DatePicker
          value={dateValue}
          onChange={(date) => {
            if (!readonly && date) {
              session.setImpDate(toWallClockDate(date));
            }
          }}
          disabled={readonly}
          className="h-10 w-40"
        />
      </div>

      <LookupDropdown
        label="Site"
        value={session.siteId}
        options={sites}
        loading={sitesLoading}
        disabled={readonly}
        placeholder="Select site"
        onChange={session.setSiteId}
        className="w-48"
        filter
      />

      <LookupDropdown
        label="Yard"
        value={session.yardId}
        options={yards}
        loading={yardsLoading}
        disabled={readonly}
        placeholder="Select yard"
        onChange={session.setYardId}
        className="w-48"
        filter
      />

      <LookupDropdown
        label="Vet"
        value={session.vetId}
        options={vets}
        loading={vetsLoading}
        disabled={readonly}
        placeholder="Select vet"
        onChange={session.setVetId}
        className="w-40"
        filter
      />

      <LookupDropdown
        label="Tech"
        value={session.techId}
        options={techs}
        loading={techsLoading}
        disabled={readonly}
        placeholder="Select tech"
        onChange={session.setTechId}
        className="w-40"
        filter
      />

      <div className="flex flex-col gap-1">
        <span className="text-muted-foreground text-[11px] font-medium tracking-wide uppercase">
          Chute
        </span>
        <div className="flex">
          {CHUTE_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              disabled={readonly}
              onClick={() => session.setChuteNumber(opt.value)}
              style={{ height: '2.4rem' }}
              className={`flex items-center border px-4 text-sm font-medium transition-colors first:rounded-l-md last:rounded-r-md ${
                session.chuteNumber === opt.value
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'border-input bg-background hover:bg-muted -ml-px'
              } disabled:pointer-events-none disabled:opacity-50`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-1">
        <span className="text-muted-foreground text-[11px] font-medium tracking-wide uppercase">
          Recip
        </span>
        <button
          type="button"
          disabled={readonly}
          aria-pressed={session.clientRecipMode}
          onClick={() => session.setClientRecipMode(!session.clientRecipMode)}
          style={{ height: '2.4rem' }}
          className={`flex items-center rounded-md border px-4 text-sm font-medium transition-colors ${
            session.clientRecipMode
              ? 'border-amber-500 bg-amber-500 text-white hover:bg-amber-600'
              : 'border-input bg-background hover:bg-muted'
          } disabled:pointer-events-none disabled:opacity-50`}
          title={
            session.clientRecipMode
              ? 'Client Recip mode is ON — new queue entries accept a free-text client name'
              : 'Enable Client Recip mode to add a free-text client name to the queue'
          }
        >
          Client Recip
        </button>
      </div>

      {actions && <div className="ml-auto flex items-end gap-2">{actions}</div>}
    </div>
  );
}

function LookupDropdown({
  label,
  value,
  options,
  loading,
  disabled,
  placeholder,
  onChange,
  className,
  filter = false,
}: {
  label: string;
  value: string;
  options: LookupItem[];
  loading: boolean;
  disabled: boolean;
  placeholder: string;
  onChange: (id: string) => void;
  className?: string;
  filter?: boolean;
}) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-muted-foreground text-[11px] font-medium tracking-wide uppercase">
        {label}
      </span>
      <Dropdown
        value={value}
        options={options}
        optionLabel="name"
        optionValue="id"
        placeholder={placeholder}
        loading={loading}
        disabled={disabled}
        filter={filter}
        showClear
        className={className}
        onChange={(e) => onChange(e.value ?? '')}
        virtualScrollerOptions={{ itemSize: 32 }}
        scrollHeight="300px"
      />
    </div>
  );
}
